import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form, Formik } from 'formik';
import { get, set } from 'lodash';
import moment from 'moment';
import * as Yup from 'yup';

import { appointmentTypesResource, telehealthsResources } from '@lib/resources';
import { selectCurrentUser, selectCurrentUserSettings } from '@lib/selectors';
import storage from '@lib/services/storageService';

import SessionInfoStep from 'components/DialogVideoOnDemandSessionCreation/components/SessionInfoStep';
import SessionLinkStep from 'components/DialogVideoOnDemandSessionCreation/components/SessionLinkStep';
import SessionTypeStep from 'components/DialogVideoOnDemandSessionCreation/components/SessionTypeStep';

export const LS_ON_DEMAND_SESSIONS_INFO = 'lh:on_demand_sessions_info';
export const FORM_NAME = 'formDialogVideoOnDemandSessionCreation';
export const SESSION_TYPE = {
  telehealth: 'telehealth',
  video: 'video',
};
export const DIALOG_STEP = {
  type: 'type',
  info: 'info',
  link: 'link',
};

export interface DialogVideoOnDemandSessionCreationProps {
  onClose: () => void;
  patient: string;
}

const APPOINTMENT_TYPES_RESULT_KEY =
  'components/DialogVideoOnDemandSessionCreation/components/SessionInfoStep/appointmentTypes';

const DialogVideoOnDemandSessionCreation: React.FC<DialogVideoOnDemandSessionCreationProps> = ({
  onClose,
  patient,
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const settings = useSelector(selectCurrentUserSettings);
  const [isAppointmentTypeEnabled, setIsAppointmentTypeEnabled] = useState(false);
  const isProvider = Boolean(currentUser && currentUser.type === 'doctor');
  const isZoomCall = settings.telehealth.vendor === 'zoom';
  let initialValues = {
    data: {},
    facility: '',
    provider: '',
    appointmentType: '',
    sessionType: isProvider ? SESSION_TYPE.video : SESSION_TYPE.telehealth,
    step: isProvider || isZoomCall ? DIALOG_STEP.info : DIALOG_STEP.type,
  };

  const { items: appointmentTypes } = useSelector(
    appointmentTypesResource.makeSelectPage(APPOINTMENT_TYPES_RESULT_KEY),
  );

  useEffect(() => {
    if (!isAppointmentTypeEnabled && appointmentTypes && appointmentTypes.length > 1) setIsAppointmentTypeEnabled(true);
  }, [appointmentTypes, isAppointmentTypeEnabled]);

  const settingOnDemandSessionsInfo: any = storage.get(LS_ON_DEMAND_SESSIONS_INFO) || {};
  const currentUserOnDemandSessionsInfo: any =
    (currentUser && get(settingOnDemandSessionsInfo, currentUser.id, {})) || {};
  const patientSessionInfo: any = get(currentUserOnDemandSessionsInfo, patient, {}) || {};

  if (patientSessionInfo && patientSessionInfo.expiresAt) {
    if (moment().isAfter(moment(patientSessionInfo.expiresAt))) {
      // clear the patient/current user session info state if expired
      set(currentUserOnDemandSessionsInfo, patient, {});
      if (currentUser) set(settingOnDemandSessionsInfo, currentUser.id, currentUserOnDemandSessionsInfo);
      storage.set(LS_ON_DEMAND_SESSIONS_INFO, settingOnDemandSessionsInfo);
    } else {
      initialValues = {
        data: patientSessionInfo.data,
        facility: patientSessionInfo.facility,
        provider: patientSessionInfo.provider,
        appointmentType: patientSessionInfo.appointmentType,
        sessionType: patientSessionInfo.sessionType,
        step: patientSessionInfo.step,
      };
    }
  }

  const validationSchema = Yup.object().shape({
    sessionType: Yup.string(),
    type: Yup.string(),
    facility: Yup.string().when('step', {
      is: DIALOG_STEP.info,
      then: Yup.string().required('Required'),
    }),
    provider: Yup.string().when('step', {
      is: DIALOG_STEP.info,
      then: Yup.string().when('sessionType', {
        is: SESSION_TYPE.telehealth,
        then: Yup.string().required('Required'),
      }),
    }),
    appointmentType: Yup.string().when('step', {
      is: DIALOG_STEP.info,
      then: Yup.string().when('appointmentType', {
        is: (value: string) => !value && isAppointmentTypeEnabled,
        then: Yup.string().required('Required'),
      }),
    }),
  });

  const isInitialValid = validationSchema.isValidSync(initialValues);

  const handleSubmit = (values: any, { setFieldTouched, setFieldValue, setSubmitting }: any) => {
    if (values.step === DIALOG_STEP.type && values.sessionType === SESSION_TYPE.telehealth) {
      setFieldValue('step', DIALOG_STEP.info);
      setFieldTouched('facility', false);
      setFieldTouched('provider', false);
      setFieldTouched('appointmentType', false);
      setSubmitting(false);
    } else {
      const body: any = { patientId: patient };

      if (values.sessionType === SESSION_TYPE.video && !isProvider) body.staffId = currentUser ? currentUser.id : '';
      if (values.provider || isProvider) body.providerId = values.provider || (currentUser ? currentUser.id : '');
      if (values.facility) body.facilityId = values.facility;
      if (values.appointmentType) body.appointmentTypeId = values.appointmentType;

      dispatch(
        telehealthsResources.request({
          method: 'post',
          url: '/telehealths/session/adhoc',
          data: body,
          meta: {
            callbackSuccess: (response: any) => {
              const { data } = response;
              const sessionData = {
                ...values,
                data,
                expiresAt: moment()
                  .add(30, 'minutes')
                  .toDate(),
                step: DIALOG_STEP.link,
              };

              set(currentUserOnDemandSessionsInfo, patient, sessionData);
              if (currentUser) set(settingOnDemandSessionsInfo, currentUser.id, currentUserOnDemandSessionsInfo);

              storage.set(LS_ON_DEMAND_SESSIONS_INFO, settingOnDemandSessionsInfo);

              setFieldValue('data', data || {});
              setSubmitting(false);
              setFieldValue('step', DIALOG_STEP.link);
            },
            callbackError: () => {
              setSubmitting(false);
            },
          },
        }),
      );
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      isInitialValid={isInitialValid}
      onSubmit={handleSubmit}
    >
      {(props) => {
        const {
          values: { step },
        } = props;

        return (
          <Form id={FORM_NAME}>
            {step === DIALOG_STEP.type && <SessionTypeStep formProps={props} onClose={onClose} />}
            {step === DIALOG_STEP.info && (
              <SessionInfoStep
                formProps={props}
                isProvider={isProvider}
                isAppointmentTypeEnabled={isAppointmentTypeEnabled}
                onClose={onClose}
              />
            )}
            {step === DIALOG_STEP.link && <SessionLinkStep formProps={props} isProvider={isProvider} />}
          </Form>
        );
      }}
    </Formik>
  );
};

export default DialogVideoOnDemandSessionCreation;
